import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  Loader2,
  Phone,
  PhoneForwarded,
  UserCircle,
  Users,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  useAvailableTeamAgents,
  useTeamGroups,
} from '@/api/teamGroups'
import {
  newTransferAction,
  transferActionSchema,
  type RingStrategy,
  type TransferAction,
  type TransferMode,
  type TransferType,
} from './actionsSchemas'

interface Props {
  isOpen: boolean
  /** Langue de l'agent : la phrase par défaut est dite dans cette langue, pas dans celle du dashboard. */
  agentLanguage?: string
  onClose: () => void
  onSave: (action: TransferAction) => void
  action?: TransferAction | null
}

export function TransferActionModal({ isOpen, agentLanguage, onClose, onSave, action }: Props) {
  const { t, i18n } = useTranslation()

  // Données pour les selects (web agents + teams) chargées via TanStack Query.
  const { data: availableAgents, isLoading: agentsLoading } = useAvailableTeamAgents(isOpen)
  const { data: teams = [], isLoading: teamsLoading } = useTeamGroups()
  const webAgents = availableAgents?.web_agents ?? []

  const form = useForm<TransferAction>({
    resolver: zodResolver(transferActionSchema),
    defaultValues: action ?? newTransferAction(),
  })
  const { watch, setValue, register, control, handleSubmit, formState } = form

  const [options, setOptions] = useState(false)
  const reponseParDefaut = i18n.getFixedT(agentLanguage === 'en' ? 'en' : 'fr')('manager.aiAgents.transferModal.agentResponseDefault', 'Je vous transfère, un instant.')
  // Nouvelle action : la phrase de l'agent est déjà remplie, on voit ce qui sera dit.
  useEffect(() => {
    if (isOpen) { form.reset(action ?? { ...newTransferAction(), agent_response: reponseParDefaut }); setOptions(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, action, form])

  const transferType = watch('transfer_type')
  const transferMode = watch('transfer_mode')
  const ringStrategy = watch('ring_strategy')
  const transferTo = watch('transfer_to')

  const onSubmit = (values: TransferAction) => {
    onSave(values)
    onClose()
  }

  const errMsg = (key?: string) =>
    key ? t(`manager.aiAgents.errors.${key}`) : null

  const handleTransferTypeChange = (value: TransferType) => {
    if (value === 'main_line') {
      setValue('transfer_type', value, { shouldDirty: true })
      setValue('transfer_to', 'main_line', { shouldDirty: true })
      setValue('transfer_to_name', t('manager.aiAgents.transferModal.mainLineName'), {
        shouldDirty: true,
      })
    } else {
      setValue('transfer_type', value, { shouldDirty: true })
      setValue('transfer_to', '', { shouldDirty: true })
      setValue('transfer_to_name', '', { shouldDirty: true })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[90vh] overflow-hidden">
        <form
          // stopPropagation : sinon le submit du modal bubble dans la React
          // virtual tree (via Portal Radix) et déclenche aussi le submit du
          // form parent AIAgentFormView = update global agent non voulu.
          onSubmit={(e) => {
            e.stopPropagation()
            // Nom et phrase de l'agent ne sont pas demandés : remplis avant validation.
            if (!form.getValues('name').trim()) setValue('name', form.getValues('scenario').trim().slice(0, 60) || t('manager.aiAgents.transferModal.defaultName', 'Transfert'))
            if (!form.getValues('agent_response').trim()) setValue('agent_response', reponseParDefaut)
            return handleSubmit(onSubmit)(e)
          }}
          className="flex flex-col max-h-[90vh]"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
                <PhoneForwarded className="h-5 w-5 text-blue-600" />
              </div>
              {action
                ? t('manager.aiAgents.transferModal.editTitle')
                : t('manager.aiAgents.transferModal.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {/* Section 1 — Infos de base */}
            <section className="space-y-4">
              <div>
                <Label htmlFor="transfer-scenario" className="text-sm font-medium">
                  {t('manager.aiAgents.transferModal.scenario')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="transfer-scenario"
                  placeholder={t('manager.aiAgents.transferModal.scenarioPlaceholder')}
                  className="mt-2 min-h-[80px] resize-y"
                  {...register('scenario', { onBlur: (e) => { if (!form.getValues('name').trim()) setValue('name', e.target.value.trim().slice(0, 60)) } })}
                />
                {formState.errors.scenario && (
                  <p className="text-xs text-red-500 mt-1">
                    {errMsg(formState.errors.scenario.message)}
                  </p>
                )}
              </div>

            </section>

            {/* Section 2 — Destination */}
            <section className="space-y-4 pt-4 border-t dark:border-slate-800">
              <div>
                <Label className="text-sm font-medium">
                  {t('manager.aiAgents.transferModal.destinationType')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Controller
                  control={control}
                  name="transfer_type"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(v) =>
                        handleTransferTypeChange(v as TransferType)
                      }
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">
                          <span className="flex items-center gap-2">
                            <UserCircle className="h-4 w-4" />
                            {t('manager.aiAgents.transferModal.typeAgent')}
                          </span>
                        </SelectItem>
                        <SelectItem value="team">
                          <span className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            {t('manager.aiAgents.transferModal.typeTeam')}
                          </span>
                        </SelectItem>
                        <SelectItem value="external_number">
                          <span className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {t('manager.aiAgents.transferModal.typeExternal')}
                          </span>
                        </SelectItem>
                        <SelectItem value="main_line">
                          <span className="flex items-center gap-2">
                            <PhoneForwarded className="h-4 w-4" />
                            {t('manager.aiAgents.transferModal.typeMainLine')}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {transferType === 'agent' && (
                <div>
                  <Label className="text-sm font-medium">
                    {t('manager.aiAgents.transferModal.pickAgent')}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  {agentsLoading ? (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('manager.aiAgents.transferModal.loadingAgents')}
                    </div>
                  ) : (
                    <Select
                      value={transferTo}
                      onValueChange={(v) => {
                        const agent = webAgents.find((a) => `web_${a.id}` === v)
                        setValue('transfer_to', v, { shouldDirty: true })
                        setValue('transfer_to_name', agent?.name ?? '', {
                          shouldDirty: true,
                        })
                      }}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue
                          placeholder={t(
                            'manager.aiAgents.transferModal.pickAgentPlaceholder',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {webAgents.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            {t('manager.aiAgents.transferModal.noAgents')}
                          </div>
                        ) : (
                          webAgents.map((a) => (
                            <SelectItem key={`web_${a.id}`} value={`web_${a.id}`}>
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                                {a.name || a.email}
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                  {formState.errors.transfer_to && (
                    <p className="text-xs text-red-500 mt-1">
                      {errMsg(formState.errors.transfer_to.message)}
                    </p>
                  )}
                </div>
              )}

              {transferType === 'team' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">
                      {t('manager.aiAgents.transferModal.pickTeam')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    {teamsLoading ? (
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('manager.aiAgents.transferModal.loadingTeams')}
                      </div>
                    ) : (
                      <Select
                        value={transferTo}
                        onValueChange={(v) => {
                          const team = teams.find((tm) => String(tm.id) === v)
                          setValue('transfer_to', v, { shouldDirty: true })
                          setValue('transfer_to_name', team?.name ?? '', {
                            shouldDirty: true,
                          })
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue
                            placeholder={t(
                              'manager.aiAgents.transferModal.pickTeamPlaceholder',
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              {t('manager.aiAgents.transferModal.noTeams')}
                            </div>
                          ) : (
                            teams.map((tm) => (
                              <SelectItem key={tm.id} value={String(tm.id)}>
                                <span className="flex items-center gap-2">
                                  <Users className="h-4 w-4 text-emerald-500" />
                                  {tm.name}
                                  <span className="text-xs text-gray-400">
                                    ({tm.member_count}{' '}
                                    {t('manager.aiAgents.transferModal.members', {
                                      count: tm.member_count,
                                    })}
                                    )
                                  </span>
                                </span>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    {formState.errors.transfer_to && (
                      <p className="text-xs text-red-500 mt-1">
                        {errMsg(formState.errors.transfer_to.message)}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">
                      {t('manager.aiAgents.transferModal.ringStrategy')}
                    </Label>
                    <Controller
                      control={control}
                      name="ring_strategy"
                      render={({ field }) => (
                        <Select
                          value={field.value ?? 'PARALLEL'}
                          onValueChange={(v) => field.onChange(v as RingStrategy)}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PARALLEL">
                              {t('manager.aiAgents.transferModal.ringParallel')}
                            </SelectItem>
                            <SelectItem value="SEQUENTIAL">
                              {t('manager.aiAgents.transferModal.ringSequential')}
                            </SelectItem>
                            <SelectItem value="RANDOM">
                              {t('manager.aiAgents.transferModal.ringRandom')}
                            </SelectItem>
                            <SelectItem value="ROUND_ROBIN">
                              {t('manager.aiAgents.transferModal.ringRoundRobin')}
                            </SelectItem>
                            <SelectItem value="LEAST_RECENT">
                              {t('manager.aiAgents.transferModal.ringLeastRecent')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">
                      {t('manager.aiAgents.transferModal.ringTimeout')}
                    </Label>
                    <Input
                      type="number"
                      min={5}
                      max={60}
                      {...register('ring_timeout', {
                        valueAsNumber: true,
                      })}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {ringStrategy === 'PARALLEL'
                        ? t('manager.aiAgents.transferModal.ringTimeoutHelpParallel')
                        : t('manager.aiAgents.transferModal.ringTimeoutHelp')}
                    </p>
                  </div>
                </div>
              )}

              {transferType === 'external_number' && (
                <div>
                  <Label htmlFor="transfer-to" className="text-sm font-medium">
                    {t('manager.aiAgents.transferModal.externalNumber')}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="transfer-to"
                    placeholder="+15145551234"
                    className="mt-2"
                    {...register('transfer_to')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manager.aiAgents.transferModal.externalNumberHelp')}
                  </p>
                  {formState.errors.transfer_to && (
                    <p className="text-xs text-red-500 mt-1">
                      {errMsg(formState.errors.transfer_to.message)}
                    </p>
                  )}
                </div>
              )}

              {transferType === 'main_line' && (
                <div className="p-4 bg-blue-50 border border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {t('manager.aiAgents.transferModal.mainLineHelp')}
                  </p>
                </div>
              )}
            </section>

            <div className="border-t dark:border-slate-800 pt-3">
              <button type="button" onClick={() => setOptions(!options)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                <ChevronDown className={cn('h-4 w-4 transition-transform', options && 'rotate-180')} />{t('manager.aiAgents.transferModal.options', 'Options')}
              </button>
            </div>
            {options && (
              <>
            <section className="space-y-4">
              <div>
                <Label htmlFor="transfer-response" className="text-sm font-medium">
                  {t('manager.aiAgents.transferModal.agentResponse')}
                </Label>
                <Textarea
                  id="transfer-response"
                  
                  className="mt-2 min-h-[80px] resize-y"
                  {...register('agent_response')}
                />
                {formState.errors.agent_response && (
                  <p className="text-xs text-red-500 mt-1">
                    {errMsg(formState.errors.agent_response.message)}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="transfer-name" className="text-sm font-medium">
                  {t('manager.aiAgents.transferModal.name')}
                </Label>
                <Input
                  id="transfer-name"
                  placeholder={t('manager.aiAgents.transferModal.namePlaceholder')}
                  className="mt-2"
                  {...register('name')}
                />
                {formState.errors.name && (
                  <p className="text-xs text-red-500 mt-1">
                    {errMsg(formState.errors.name.message)}
                  </p>
                )}
              </div>

            </section>
            {/* Section 3 — Mode de transfert */}
            <section className="space-y-4 pt-4 border-t dark:border-slate-800">
              <h3 className="font-medium text-sm text-gray-700 dark:text-slate-300">{t('manager.aiAgents.transferModal.modeSection')}</h3>

              <div className="grid grid-cols-2 gap-4">
                {(['cold', 'warm'] as TransferMode[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setValue('transfer_mode', mode, { shouldDirty: true })}
                    className={cn(
                      'p-4 border-2 rounded-lg text-left transition-all',
                      transferMode === mode
                        ? mode === 'warm'
                          ? 'border-green-500 bg-green-50 dark:bg-green-500/10'
                          : 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300',
                    )}
                  >
                    <div className="font-medium text-sm mb-1">
                      {mode === 'cold'
                        ? t('manager.aiAgents.transferModal.modeCold')
                        : t('manager.aiAgents.transferModal.modeWarm')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {mode === 'cold'
                        ? t('manager.aiAgents.transferModal.modeColdDesc')
                        : t('manager.aiAgents.transferModal.modeWarmDesc')}
                    </div>
                  </button>
                ))}
              </div>

              {transferMode === 'warm' && (
                <div className="space-y-4 mt-4 p-4 bg-green-50 dark:bg-green-500/10 rounded-lg border border-green-200 dark:border-green-500/30">
                  <div>
                    <Label htmlFor="context-prompt" className="text-sm font-medium">
                      {t('manager.aiAgents.transferModal.contextPrompt')}
                    </Label>
                    <Textarea
                      id="context-prompt"
                      placeholder={t(
                        'manager.aiAgents.transferModal.contextPromptPlaceholder',
                      )}
                      className="mt-2 min-h-[80px] resize-y bg-white dark:bg-slate-900"
                      {...register('context_prompt')}
                    />
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                      {t('manager.aiAgents.transferModal.contextPromptHelp')}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="handoff-message" className="text-sm font-medium">
                      {t('manager.aiAgents.transferModal.handoffMessage')}
                    </Label>
                    <Textarea
                      id="handoff-message"
                      placeholder={t(
                        'manager.aiAgents.transferModal.handoffPlaceholder',
                      )}
                      className="mt-2 min-h-[100px] resize-y bg-white dark:bg-slate-900"
                      {...register('handoff_message')}
                    />
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                      {t('manager.aiAgents.transferModal.handoffHelp')}
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* Section 4 — Options avancées */}
            <section className="space-y-4 pt-4 border-t dark:border-slate-800">
              <h3 className="font-medium text-sm text-gray-700 dark:text-slate-300">{t('manager.aiAgents.transferModal.advancedSection')}</h3>

              <div>
                <Label htmlFor="fallback-message" className="text-sm font-medium">
                  {t('manager.aiAgents.transferModal.fallbackMessage')}
                </Label>
                <Textarea
                  id="fallback-message"
                  placeholder={t(
                    'manager.aiAgents.transferModal.fallbackPlaceholder',
                  )}
                  className="mt-2 min-h-[80px] resize-y"
                  {...register('fallback_message')}
                />
              </div>
            </section>
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t dark:border-slate-800 flex justify-end gap-3 bg-gray-50 dark:bg-slate-800/50">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {action
                ? t('manager.aiAgents.update')
                : t('manager.aiAgents.transferModal.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
